import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import logo from "../../../core/img/texas-alarms-logo.jpg";
import { useLoginMutation } from "../authApi";
import { credentialsSet, selectAuth, selectIsAuthenticated } from "../authSlice";
import { paths } from "../../../routes/paths";

const SignIn = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: "onChange" });
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [login, { isLoading }] = useLoginMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { mustChangePassword } = useSelector(selectAuth);

  // A login on a temporary password (an admin reset it) goes to Change
  // Password first. Both redirects below - this render-time one and the
  // navigate() after a successful login - use the same rule, so they can't
  // race each other to two different pages.
  const afterSignIn = (mustChange) =>
    mustChange ? paths.changePassword : location.state?.from?.pathname || paths.dashboard;

  if (isAuthenticated) {
    return <Navigate to={afterSignIn(mustChangePassword)} replace />;
  }

  const onSubmit = async ({ username, password }) => {
    try {
      const data = await login({ username, password }).unwrap();
      dispatch(
        credentialsSet({
          token: data.token,
          expiresAtUtc: data.expiresAtUtc,
          username: data.username,
          fullName: data.fullName,
          designation: data.designation,
          mustChangePassword: data.mustChangePassword,
        })
      );
      toast.success(`Welcome back, ${data.fullName || data.username}`);
      navigate(afterSignIn(data.mustChangePassword), { replace: true });
    } catch (error) {
      toast.error(error?.data?.message || error?.message || "Login failed");
    }
  };

  return (
    <div className="main-wrapper">
      <div className="account-content">
        <div className="login-wrapper bg-img">
          <div className="login-content">
            <div className="login-userset">
              <div className="login-logo logo-normal">
                <img src={logo} alt="Texas Alarms" />
              </div>

              <div className="login-userheading">
                <h3>Sign In</h3>
                <h4>Access the Texas Alarms admin console</h4>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <div className="form-login mb-3">
                  <label className="form-label">Username</label>
                  <div className="form-addons">
                    <input
                      type="text"
                      className="form-control"
                      autoComplete="username"
                      {...register("username", { required: true })}
                    />
                  </div>
                  {errors.username && (
                    <span className="error text-danger">
                      Username is required
                    </span>
                  )}
                </div>

                <div className="form-login mb-3">
                  <label className="form-label">Password</label>
                  <div className="pass-group">
                    <input
                      type={isPasswordVisible ? "text" : "password"}
                      className="pass-input form-control"
                      autoComplete="current-password"
                      {...register("password", { required: true })}
                    />
                    <span
                      className={`fas toggle-password ${
                        isPasswordVisible ? "fa-eye" : "fa-eye-slash"
                      }`}
                      onClick={() => setPasswordVisible((v) => !v)}
                    />
                  </div>
                  {errors.password && (
                    <span className="error text-danger">
                      Password is required
                    </span>
                  )}
                </div>

                <div className="form-login">
                  <button
                    type="submit"
                    className="btn btn-login"
                    disabled={isLoading}
                  >
                    {isLoading ? "Signing in..." : "Sign In"}
                  </button>
                </div>
              </form>

              <div className="form-sociallink">
                <div className="my-4 d-flex justify-content-center align-items-center copyright-text">
                  <p>
                    Copyright © {new Date().getFullYear()} Texas Alarms. All
                    rights reserved
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
